import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, CheckCircle, XCircle, Building2, RefreshCw, ExternalLink } from 'lucide-react';
import { API } from '../lib/constants';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import { BackMenu } from '../components/dashboard/BackMenu';

const STATUS_FILTERS = ['all', 'pending', 'approved', 'rejected'];
const STATUS_BADGE = { approved: 'badge-success', rejected: 'badge-danger', pending: 'badge-warning' };

const SuperAdminDashboard = () => {
  const { account, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = filter === 'all' ? '' : `?status=${filter}`;
      const [s, t] = await Promise.all([axios.get(`${API}/super/stats`), axios.get(`${API}/super/tenants${query}`)]);
      setStats(s.data); setTenants(t.data);
    } catch (e) {
      toast.error('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const act = async (tenantId, action) => {
    try {
      await axios.post(`${API}/super/tenants/${tenantId}/${action}`);
      toast.success(`Workspace ${action}d`);
      load();
    } catch (e) {
      toast.error(`Failed to ${action} workspace`);
    }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const STAT = [
    ['Total', stats?.total_tenants, 'text-white'],
    ['Pending', stats?.pending_tenants, 'text-amber-600'],
    ['Approved', stats?.approved_tenants, 'text-emerald-600'],
    ['Rejected', stats?.rejected_tenants, 'text-red-600'],
  ];

  return (
    <div className="min-h-screen bg-ink" data-testid="super-dashboard">
      <header className="glass-header !z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <Logo testId="super-logo" />
            <span className="badge-grape hidden sm:inline-flex">Super admin</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden md:block font-mono text-[11px] text-slate-500">{account?.email}</span>
            <BackMenu onLogout={handleLogout} testId="super-back-menu" />
            <button data-testid="super-logout-btn" onClick={handleLogout} className="btn-ghost !py-2"><LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Log out</span></button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {STAT.map(([label, value, tone], i) => (
            <div key={label} className={`card card-hover p-5 fade-up fade-up-delay-${i}`} data-testid={`super-stat-${label.toLowerCase()}`}>
              <p className="label">{label}</p>
              <p className={`font-display font-bold text-4xl tracking-tight mt-3 ${tone}`}>{value ?? '—'}</p>
            </div>
          ))}
        </div>

        <div className="card">
          <div className="px-6 py-4 border-b-2 border-line flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <h2 className="h-display text-lg flex items-center gap-2"><Building2 className="w-5 h-5 text-cyan" /> Client workspaces</h2>
            <div className="flex items-center gap-1 border-2 border-line p-1 bg-ink">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f}
                  data-testid={`filter-${f}-btn`}
                  onClick={() => setFilter(f)}
                  className={`px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-wider transition-colors ${filter === f ? 'bg-cyan text-ink' : 'text-slate-400 hover:text-white'}`}
                >
                  {f}
                </button>
              ))}
              <button data-testid="super-refresh-btn" onClick={load} className="p-1.5 text-slate-400 hover:text-cyan"><RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /></button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full table-dark" data-testid="tenants-table">
              <thead>
                <tr>{['Workspace', 'Owner', 'Portal', 'Plan', 'Faces / Photos', 'Status', ''].map((h) => <th key={h}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {tenants.length === 0 && !loading ? (
                  <tr><td colSpan={7} className="text-center py-14 font-mono text-xs uppercase tracking-[0.2em] text-slate-500" data-testid="no-tenants-msg">No workspaces found</td></tr>
                ) : (
                  tenants.map((t, i) => (
                    <tr key={t.tenant_id} data-testid={`tenant-row-${i}`}>
                      <td className="font-semibold text-white">{t.name}</td>
                      <td>{t.owner?.email || '—'}</td>
                      <td>
                        <a href={`/t/${t.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 font-mono text-xs bg-ink border border-line hover:border-cyan text-cyan px-2 py-1 transition-colors">
                          /t/{t.slug} <ExternalLink className="w-3 h-3" />
                        </a>
                      </td>
                      <td><span className={t.plan === 'paid' ? 'badge-lime' : 'badge-muted'}>{t.plan === 'paid' ? 'Event Pass' : 'Free'}</span></td>
                      <td>{t.registered_faces} / {t.photos}</td>
                      <td><span className={STATUS_BADGE[t.approval_status] || 'badge-muted'}>{t.approval_status}</span></td>
                      <td>
                        <div className="flex gap-2 justify-end">
                          {t.approval_status !== 'approved' && (
                            <button data-testid={`approve-${i}-btn`} onClick={() => act(t.tenant_id, 'approve')} className="inline-flex items-center gap-1.5 px-3 py-1.5 font-display font-bold uppercase tracking-wider text-[11px] bg-lime text-ink border-2 border-lime hover:bg-lime/90 transition-colors">
                              <CheckCircle className="w-3.5 h-3.5" /> Approve
                            </button>
                          )}
                          {t.approval_status !== 'rejected' && (
                            <button data-testid={`reject-${i}-btn`} onClick={() => act(t.tenant_id, 'reject')} className="btn-danger !py-1.5 !px-3 !text-[11px]">
                              <XCircle className="w-3.5 h-3.5" /> Reject
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SuperAdminDashboard;
